import PQueue from "p-queue"
import { clamp } from "@/utils/math"
import { Sentry } from "@/telemetry/sentry"
import { registerSentryContextTarget } from "@/telemetry/sentry-peers"
import type { PixelData } from "@/core/utils/image-data"
import { Deferred } from "@/utils/deferred"
import {
	encodePSD as encodePSDBlob,
	encodePSDData as encodePSDPayload,
	getPSDBufferByteLengths,
	type EncodedPSDData,
	type PSDEncodeOptions,
	type PSDEncoderLike,
} from "@/core/encoders/psd-encoder"

export interface PSDEncoderOptions {
	jobs: number
}

interface PSDBufferReservationOptions {
	pixelByteLength: number
	scratchByteLength: number
}

export interface PSDRGBDataBufferLease extends PSDEncoderLike {
	data: Uint8Array | Uint16Array
	bitDepth: 8 | 16
	encodePSDData(pixelData: PixelData, options?: PSDEncodeOptions): Promise<EncodedPSDData>
	release(): Promise<void>
}

interface PSDArena {
	pixelBuffer: ArrayBuffer
	scratchBuffer: ArrayBuffer
}

class PSDEncoderController implements PSDEncoderLike {
	public readonly maxConcurrency = 2

	private queue = new PQueue()
	private concurrency = 1
	private arenas: PSDArena[] = []
	private idleArenas: PSDArena[] = []

	public constructor() {
		this.setOptions({ jobs: 1 })
	}

	public setSentryContext() {
		// The PSD controller is main-thread only for now; this keeps it compatible
		// with the shared encoder lifecycle hooks.
	}

	public disableSentry() {
		// No worker Sentry state to disable yet.
	}

	public setOptions(options: PSDEncoderOptions) {
		this.concurrency = clamp(options.jobs, 1, this.maxConcurrency)
		this.queue.clear()
		this.queue = new PQueue({ concurrency: this.concurrency })
		this.queue.on("error", (e) => Sentry.captureException(e))
		this.resetArenas()
	}

	public clearQueue() {
		this.queue.clear()
		this.resetArenas()
	}

	public releaseBuffers() {
		if (this.idleArenas.length !== this.arenas.length) {
			return
		}

		this.resetArenas()
	}

	public reserveBuffers(options: PSDBufferReservationOptions) {
		if (this.idleArenas.length !== this.arenas.length) {
			throw new Error("Cannot reserve PSD buffers while an export is active")
		}

		for (const arena of this.arenas) {
			if (arena.pixelBuffer.byteLength < options.pixelByteLength) {
				arena.pixelBuffer = new ArrayBuffer(options.pixelByteLength)
			}
			if (arena.scratchBuffer.byteLength < options.scratchByteLength) {
				arena.scratchBuffer = new ArrayBuffer(options.scratchByteLength)
			}
		}
	}

	public async encodePSD(pixelData: PixelData, options?: PSDEncodeOptions): Promise<Blob> {
		// Avoid direct use: the arena-backed PSD export path expects callers to acquire
		// buffers first and encode through the lease's encodePSDData().
		return await this.queue.add(
			async () => {
				const arena = this.acquireArena()
				try {
					const { scratchByteLength } = getPSDBufferByteLengths({
						width: pixelData.width,
						height: pixelData.height,
						bitDepth: pixelData.data instanceof Uint16Array ? 16 : 8,
						targetChunkByteLength: options?.targetChunkByteLength,
					})
					this.ensureArenaScratchCapacity(arena, scratchByteLength)
					return await encodePSDBlob(pixelData, {
						...options,
						scratchBufferProvider: (byteLength) =>
							this.getArenaScratchView(arena, byteLength),
					})
				} finally {
					this.releaseArena(arena)
				}
			},
			{ timeout: 30000 },
		)
	}

	public async acquireRGBDataBuffer(options: {
		pixelByteLength: number
		bitDepth: 8 | 16
		scratchByteLength: number
	}): Promise<PSDRGBDataBufferLease> {
		const leaseReady = new Deferred<PSDRGBDataBufferLease>()
		const leaseReleased = new Deferred<void>()
		const leaseFreed = new Deferred<void>()

		void this.queue
			.add(async () => {
				const arena = this.acquireArena()

				try {
					this.ensureArenaPixelCapacity(arena, options.pixelByteLength)
					this.ensureArenaScratchCapacity(arena, options.scratchByteLength)
					const data = this.getArenaPixelView(
						arena,
						options.pixelByteLength,
						options.bitDepth,
					)
					let released = false

					const release = async () => {
						if (released) {
							await leaseFreed.promise
							return
						}

						released = true
						leaseReleased.resolve()
						await leaseFreed.promise
					}

					leaseReady.resolve({
						data,
						bitDepth: options.bitDepth,
						release,
						encodePSD: async (pixelData, encodeOptions) =>
							await encodePSDBlob(pixelData, {
								...encodeOptions,
								scratchBufferProvider: (byteLength) =>
									this.getArenaScratchView(arena, byteLength),
							}),
						encodePSDData: (pixelData, encodeOptions) =>
							Promise.resolve(
								encodePSDPayload(pixelData, {
									...encodeOptions,
									scratchBufferProvider: (byteLength) =>
										this.getArenaScratchView(arena, byteLength),
								}),
							),
					})

					await leaseReleased.promise
				} catch (error) {
					leaseReady.reject(error)
					throw error
				} finally {
					this.releaseArena(arena)
					leaseFreed.resolve()
				}
			})
			.catch((error) => {
				leaseReady.reject(error)
			})

		return await leaseReady.promise
	}

	public getTotalBytesUsage() {
		return Promise.resolve(
			this.arenas.reduce(
				(total, arena) =>
					total + arena.pixelBuffer.byteLength + arena.scratchBuffer.byteLength,
				0,
			),
		)
	}

	private resetArenas() {
		this.arenas = Array.from({ length: this.concurrency }, () => ({
			pixelBuffer: new ArrayBuffer(0),
			scratchBuffer: new ArrayBuffer(0),
		}))
		this.idleArenas = [...this.arenas]
	}

	private acquireArena() {
		const arena = this.idleArenas.pop()
		if (!arena) {
			throw new Error("PSD encoder arena unavailable")
		}

		return arena
	}

	private releaseArena(arena: PSDArena) {
		this.idleArenas.push(arena)
	}

	private ensureArenaPixelCapacity(arena: PSDArena, byteLength: number) {
		if (byteLength > arena.pixelBuffer.byteLength) {
			throw new Error("PSD pixel arena is smaller than the requested export buffer")
		}
	}

	private ensureArenaScratchCapacity(arena: PSDArena, byteLength: number) {
		if (byteLength > arena.scratchBuffer.byteLength) {
			throw new Error("PSD scratch arena is smaller than the requested chunk")
		}
	}

	private getArenaPixelView(arena: PSDArena, byteLength: number, bitDepth: 8 | 16) {
		this.ensureArenaPixelCapacity(arena, byteLength)

		if (bitDepth === 16) {
			return new Uint16Array(arena.pixelBuffer, 0, byteLength >> 1)
		}

		return new Uint8Array(arena.pixelBuffer, 0, byteLength)
	}

	private getArenaScratchView(arena: PSDArena, byteLength: number) {
		this.ensureArenaScratchCapacity(arena, byteLength)

		return new Uint8Array(arena.scratchBuffer, 0, byteLength)
	}
}

export const PSDEncoder = new PSDEncoderController()

PSDEncoder.setOptions({ jobs: 1 })
registerSentryContextTarget(PSDEncoder)
